import {toTitleCase, getMonthsIndexDifference, getCurrentGame, commaSeparate} from "./helpers.js";
import {
    getCurrentMonth, getCompanyLatestEvent, getCompanyMainRosterSuperstars,
    getHighestLevelStaffMemberByRole, getCompanyMonth
} from "./companies.js";
import {getInjuryReturnTime} from "./superstars.js";
import {globalUser} from "./globals.js";
import {getGameCurrentUserCompany} from "./games.js";
import {getCompanyCompanyAlerts, getCompanySentNegotiations, getCompanyStaff} from "../models/company.js";
import {getNegotiationTalent} from "../models/negotiation.js";
import {getCompanyTalentRequestsData} from "./talentRequests.js";

async function getDashboardData(userCompany = null, game = null) {  // Previously get_dashboard_data

    // Get current game and company
    if (!game) {
        game = await getCurrentGame();
    }
    if (!userCompany) {
        userCompany = await getGameCurrentUserCompany(game);
    }

    // Define data structure
    let data = {
        "alerts": {
            "unhappy": [],
            "notUnhappy": [],
            "leftCompany": [],
            "injured": [],
            "injuryRecovered": [],
            "workableInjuryRecovered": [],
            "superstarRetired": [],
            "staffMemberRetired": [],
            "leavingCompany": [],
            "notLeavingCompany": [],
            "randomEvent": [],
            "facilityUpgradeAvailable": [],
            "buyoutAvailable": [],
            "companyFormed": [],
            "companyFolded": [],
            "talentOfferAccepted": [],
            "talentOfferRejected": [],
            "goalCompleted": [],
            "goalFailed": [],
            "talentRequestCompleted": [],
            "talentRequestFailed": []
        },
        "upcomingMatters": [],
        "unavailable": {
            "kayfabeInjured": [],
            "suspended": [],
            "notAvailableStaff": [],
            "notAvailable": [],
            "noAppearances": []
        },
        "injured": [],
        "tasksProgress": [],
        "talentRequests": []
    };

    const mainRosterSuperstars = await getCompanyMainRosterSuperstars(userCompany);
    const nextEvent = await getCompanyLatestEvent(userCompany);
    const companyAlerts = await getCompanyCompanyAlerts(userCompany);
    const staffMembers = (await getCompanyStaff(userCompany)).filter(x => !x.retired);
    let gameMonth = game.monthCount === 0 ? 12 : game.monthCount;
    let companyMonth = getCompanyMonth(userCompany, game) === 0 ? 12 : getCompanyMonth(userCompany, game);

    // Get company alerts by type
    for (const alertType in data.alerts) {
        if (data.alerts.hasOwnProperty(alertType)) {
            const companyAlertsOfType = companyAlerts.filter(x => x.type === alertType);
            data.alerts[alertType] = companyAlertsOfType.map(x => x.name);
        }
    }

    // Check for available callup
    if ([0, 6].includes(getCompanyMonth(userCompany, game)) && !userCompany.trainingCallupRun) {
        data['alerts']['availableCallup'] = ['Callup']
    }

    // Get events
    let companyEvents = [{'name': 'Yearly Negotiations', 'months': [12]}, {'name': 'Available Callup', 'months': [12, 6]}];
    let gameEvents = [];
    if (game.competitorsPresent) {
        gameEvents.push({'name': 'Year-End Awards', 'months': [10]})
    }
    if (!['mars', 'passion'].includes(userCompany.storyName)) {
        companyEvents.push({'name': 'Goals Review', 'months': [12]});
        if (globalUser.paid && game.settings['run_annual_tournament'] !== false) {
            gameEvents.push({'name': 'The Grand Invitational', 'months': [5]})
        }
    }

    // Check if any events are upcoming
    for (const event of gameEvents) {
        for (const month of event['months']) {
            let monthsRemaining = month - gameMonth;
            if (3 > monthsRemaining && monthsRemaining > 0) {
                data['upcomingMatters'].push({'event': event['name'], 'monthsRemaining': monthsRemaining})
            }
        }
    }
    for (const event of companyEvents) {
        for (const month of event['months']) {
            let monthsRemaining = month - companyMonth;
            if (3 > monthsRemaining && monthsRemaining > 0) {
                data['upcomingMatters'].push({'event': event['name'], 'monthsRemaining': monthsRemaining})
            }
        }
    }

    // Check for yearly rankings and yearly draft
    if (globalUser.paid && !['mars', 'passion'].includes(userCompany.storyName)) {
        // Yearly rankings
        let currentMonth = await getCurrentMonth(userCompany);
        if ([10, 11].includes(currentMonth)) {
            data['upcomingMatters'].push({'event': 'Top 50 Rankings', 'monthsRemaining': 12 - currentMonth})
        }

        // Yearly draft
        let draftSettings = userCompany.settings['yearly_brand_draft_settings'];
        if (draftSettings && draftSettings.run && draftSettings.month) {
            let draftMonth = Number.parseInt(draftSettings.month);
            let monthsRemaining = getMonthsIndexDifference(draftMonth, currentMonth);
            // Show upcoming draft if at monthly event before draft
            if ((3 > monthsRemaining && monthsRemaining > 0) || (monthsRemaining === 0 && !nextEvent.weeklyShow)) {
                data['upcomingMatters'].push({'event': 'Yearly Draft', 'monthsRemaining': monthsRemaining})
            }
        }
    }

    // Check for leaving superstars
    for (const s of mainRosterSuperstars) {
        if (s.contractMonthsRemaining !== null && (4 > s.contractMonthsRemaining && s.contractMonthsRemaining > 0)) {
            data['upcomingMatters'].push({'event': `${s.name} is leaving the company`,
                                             'monthsRemaining': s.contractMonthsRemaining})
        }
    }

    // Staff tasks progress
    for (const staffMember of staffMembers) {
        if (staffMember.currentTask) {
            data['tasksProgress'].push({
                'staffMember': staffMember.name, 'task': staffMember.currentTask,
                'monthsRemaining': staffMember.monthsLeftForTask
            })
        }
    }

    // Talent Offers sent and accepted (finalizing)
    const acceptedOffers = (await getCompanySentNegotiations(userCompany)).filter(x => x.status === 'accepted_by_talent');
    const acceptedOffersTalent = await Promise.all(
        acceptedOffers.map(x => {return getNegotiationTalent(x)})
    );
    if (acceptedOffersTalent.length > 0) {
        const talentManager = getHighestLevelStaffMemberByRole(staffMembers, 'talent_manager');
        const talentManagerName = (talentManager) ? talentManager.name : 'Staff';
        const offerWord = (acceptedOffersTalent.length === 1) ? 'deal' : 'deals';
        const acceptedOffersTalentNames = commaSeparate(acceptedOffersTalent.map(x => x.name));
        data['tasksProgress'].push({
            'staffMember': talentManagerName, 'task': `Finalizing ${offerWord} for ${acceptedOffersTalentNames}`,
            'monthsRemaining': 1
        });
    }

    // Check roster unavailability and injuries
    const doctor = getHighestLevelStaffMemberByRole(staffMembers, 'doctor');
    for (const s of mainRosterSuperstars) {
        // Injured
        if (s.injured > 0) {
            let returnTime = getInjuryReturnTime(s, doctor);
            let monthsWord = returnTime === '1' ? 'month' : 'months';
            // Add real injuries to 'injured' section
            if (s.injuryName) {
                // Show injury name if present
                let injuryName;
                if (!['minor injury', 'major injury'].includes(s.injuryName)) {
                    injuryName = toTitleCase(s.injuryName)
                }
                else {
                    injuryName = 'Injury'
                }
                // Add 'Workable' text for workable injury
                if (s.workableInjury) {
                    injuryName = 'Workable ' + injuryName
                }
                data['injured'].push({'name': s.name, 'months': s.injuryReturnMin,
                    'value': `${injuryName}: ${returnTime} ${monthsWord}`})
            }
            // Add kayfabe injuries to 'unavailable' section
            else {
                data['unavailable']['kayfabeInjured'].push({'name': s.name, 'months': s.injuryReturnMin,
                    'value': `Kayfabe: ${returnTime} ${monthsWord}`})
            }
        }
        // Suspended
        else if (s.suspensionMonths > 0) {
            let monthsWord = s.suspensionMonths === 1 ? 'month' : 'months';
            data['unavailable']['suspended'].push({'name': s.name, 'months': s.suspensionMonths,
                'value': `Suspended: ${s.suspensionMonths} ${monthsWord}`})
        }
        // Unavailable
        else if (s.unavailable) {
            data['unavailable']['notAvailable'].push(s.name)
        }
        // No appearances
        else if (s.partTimer && s.appearances === 0 && !s.fullTimeManager) {
            data['unavailable']['noAppearances'].push(s.name)
        }
    }

    // Check staff unavailability
    for (const staffMember of staffMembers) {
        if (staffMember.unavailableMonths > 0) {
            const monthsWord = (staffMember.unavailableMonths === 1) ? 'month' : 'months';
            data['unavailable']['notAvailableStaff'].push({
                'name': `${staffMember.name} (${toTitleCase(staffMember.role.replace('_', ' '))})`,
                'months': staffMember.unavailableMonths,
                'value': `Not Available: ${staffMember.unavailableMonths} ${monthsWord}`
            })
        }
    }

    // Get talent requests
    data['talentRequests'] = await getCompanyTalentRequestsData(userCompany);

    return data;
}

export {getDashboardData}